import { IonContent } from '@ionic/react';
import React from 'react';

import { ReviewCards } from '@/components/ReviewCards';
import { UiTypography } from '@/components/ui/Typography';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { AnimationFill } from '@/pages/Onboarding/components/AnimationFill';
import { useOnboarding } from '@/pages/Onboarding/context';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { OwnAiTitle, OwnAiTitleHighlight } from '../../components/Title';
import { useLayoutFormSelections } from '../../hooks/useLayoutFormSelections';

import { ScreenCreatingAiStyles } from './CreatingAi.styles';

const animationSteps = [
  {
    delay: 0,
    label: 'Applying your preferences',
  },
  {
    delay: 3,
    label: 'Initializing AI modules',
  },
  {
    delay: 6,
    label: 'Rendering high quality images',
  },
];

export const ScreenCreatingAi: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'CreatingAi',
      });
    }
  }, []);

  const { ageGeneration, bodyShape, hairColor } = useLayoutFormSelections();

  React.useEffect(() => {
    preloadImage(
      `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai-second/${ageGeneration}/${bodyShape}/${hairColor}/ai_say.jpg`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldAnimate = webCampaign.isAnimated();
  const [showElements, setShowElements] = React.useState<boolean>(true);

  const { wizard } = useOnboarding();

  const onCompleteCreating = () => {
    if (shouldAnimate) {
      setShowElements(false);
    }
    nextStepTimeout(wizard.nextStep);
  };

  return (
    <ScreenCreatingAiStyles background="dark-violet">
      <OwnAiTitle
        className="creating-ai-title"
        animate={shouldAnimate}
        show={showElements}
      >
        Blending all the AI magic to create your{' '}
        <OwnAiTitleHighlight>perfect</OwnAiTitleHighlight> partner...
      </OwnAiTitle>

      <IonContent>
        <AnimationFill
          show={showElements}
          onComplete={onCompleteCreating}
          animate={shouldAnimate}
          steps={animationSteps}
        />

        <section className="setting-up-user-reviews">
          <UiTypography variant="title-medium" align="center" transform="bold">
            Trusted by 1,556,867 users
          </UiTypography>
          <ReviewCards color="dark-violet" />
        </section>
      </IonContent>
    </ScreenCreatingAiStyles>
  );
};
